/**
 * A condition node selected on the canvas (Feature 79, revision 1, D27): that
 * condition only. Where it stands in its gate's order, its name, the steps it
 * runs, and what it tests: an if's conditions in the gate link's editor, a
 * case's values, nothing for the else. Remove condition.
 *
 * Removing the last else if, or the else, shrinks the gate back into the gate
 * link it grew from (D12), and that link is opened in its place.
 */
import { useState } from 'react';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { IconButton, Inline, Input, Stack, Typography } from '../../ui/finixt.js';
import { kindLabel, stepsOf } from './condition-words.js';
import { ELSE, conditionId, conditionsOf, findCondition } from './conditions.js';
import {
  branchRenameRefusal,
  decisionOf,
  removeBranch,
  renameBranch,
  setBranchRules,
  setCaseValues,
  setElse,
} from './decisions.js';
import type { Draft } from './draft.js';
import { InspectorPanel } from './InspectorPanel.js';
import { NameField } from './NameField.js';
import { shortRef, type Schemas } from './references.js';
import { RulesEditor } from './RulesEditor.js';
import type { Selection } from './selection.js';

/** A switch case's values as chips, and a field that adds one on Enter. */
function CaseValues({
  id,
  values,
  onChange,
}: {
  id: string;
  values: readonly string[];
  onChange: (values: string[]) => void;
}) {
  const [text, setText] = useState('');
  return (
    <Field id={id} label="Values" hint="The field must equal one of them exactly.">
      <Stack gap={7}>
        {values.length > 0 && (
          <Inline gap={5}>
            {values.map((value) => (
              <span
                key={value}
                className="mono"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '2px 2px 2px 7px',
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--surface-sunken)',
                  fontSize: 'var(--text-meta-lg)',
                }}
              >
                {value}
                <IconButton
                  icon="close"
                  size={22}
                  tone="bare"
                  aria-label={`Remove ${value}`}
                  onClick={() => onChange(values.filter((v) => v !== value))}
                />
              </span>
            ))}
          </Inline>
        )}
        <Input
          id={id}
          className="mono"
          value={text}
          placeholder="Add a value, then Enter"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !text.trim()) return;
            onChange([...values, text]);
            setText('');
          }}
        />
      </Stack>
    </Field>
  );
}

export function ConditionInspector({
  draft,
  id,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  id: string;
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  const found = findCondition(draft.decisions, id);
  if (!found) return null;
  const { decision, condition } = found;
  const gate = decision.name;
  const switching = decision.mode === 'switch';
  const branch = decision.branches.find((b) => b.name === condition.branch);
  const before = conditionsOf(decision).find(
    (c) => c.number !== null && c.number === (condition.number ?? 0) - 1,
  );

  /** Apply an edit; a gate shrunk away opens its gate link, a condition gone its gate. */
  const apply = (next: Draft) => {
    onChange(next);
    if (!decisionOf(next, gate)) {
      const link = next.links.find((l) => l.from === decision.from && l.branch === undefined);
      onSelect(link ? { kind: 'edge', from: link.from, to: link.to } : null);
    } else if (!findCondition(next.decisions, id)) onSelect({ kind: 'node', id: gate });
  };

  const where =
    condition.number === null ? (
      switching ? (
        'when no case matches'
      ) : (
        'when nothing above holds'
      )
    ) : before ? (
      <>
        tried after <span className="mono">{before.branch}</span>
      </>
    ) : (
      'tried first'
    );

  return (
    <InspectorPanel>
      <Typography variant="eyebrow">Condition node</Typography>
      <Inline gap={7} align="center" wrap={false}>
        {condition.number !== null && (
          <span className="mono" style={{ color: 'var(--text-soft)' }}>
            {condition.number}
          </span>
        )}
        <Typography variant="body" style={{ fontWeight: 'var(--weight-semibold)' }}>
          {kindLabel(condition)}
        </Typography>
      </Inline>
      <Typography variant="meta">
        In gate <span className="mono">{gate}</span>, {where}. The order is set on the gate.
      </Typography>
      <Button variant="ghost" size="sm" onClick={() => onSelect({ kind: 'node', id: gate })}>
        Open gate {gate}
      </Button>
      {branch && (
        <NameField
          id="condition-name"
          label="Name"
          value={branch.name}
          refusal={(next) => branchRenameRefusal(decision, branch.name, next)}
          onCommit={(next) => {
            onChange(renameBranch(draft, gate, branch.name, next));
            onSelect({ kind: 'node', id: conditionId(gate, next) });
          }}
        />
      )}
      <Typography variant="meta">Runs {stepsOf(draft, gate, condition.branch)}</Typography>
      {branch && switching && (
        <>
          <Typography variant="meta">
            Switch on{' '}
            <span className="mono">{decision.ref ? shortRef(decision.ref) : 'no field yet'}</span>,
            set on the gate.
          </Typography>
          <CaseValues
            id="condition-values"
            values={branch.values ?? []}
            onChange={(values) => apply(setCaseValues(draft, gate, branch.name, values))}
          />
        </>
      )}
      {branch && !switching && (
        <RulesEditor
          draft={draft}
          from={decision.from}
          rules={branch.rules ?? { join: 'all', items: [] }}
          schemas={schemas}
          id="condition"
          onChange={(rules) => apply(setBranchRules(draft, gate, branch.name, rules))}
        />
      )}
      <Button
        variant="danger"
        size="sm"
        onClick={() =>
          apply(
            condition.branch === ELSE && !branch
              ? setElse(draft, gate, false)
              : removeBranch(draft, gate, condition.branch),
          )
        }
      >
        Remove condition
      </Button>
    </InspectorPanel>
  );
}
